/**
 * CustomizerToast — a transient confirmation pill at the bottom of the customizer.
 * Self-animates in, holds, then fades out and calls onDone so the parent can clear it.
 */

import { useEffect, useRef } from 'react';
import { Animated, Text } from 'react-native';
import { darkTheme } from '../../../src/theme/themes';
import { customizerToastStyles as styles, getToastFillStyle } from './CustomizerToast.styles';

export type ToastTone = 'win' | 'info';

interface CustomizerToastProps {
  message: string;
  tone: ToastTone;
  onDone: () => void;
}

export function CustomizerToast({ message, tone, onDone }: CustomizerToastProps) {
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const anim = Animated.sequence([
      Animated.timing(progress, { toValue: 1, duration: 160, useNativeDriver: true }),
      Animated.delay(1600),
      Animated.timing(progress, { toValue: 0, duration: 220, useNativeDriver: true }),
    ]);
    anim.start(({ finished }) => {
      if (finished) onDone();
    });
    return () => anim.stop();
  }, [onDone, progress]);

  const translateY = progress.interpolate({ inputRange: [0, 1], outputRange: [8, 0] });
  const fill = getToastFillStyle(tone === 'win' ? darkTheme.secondary : darkTheme.primary);

  return (
    <Animated.View
      pointerEvents="none"
      style={[styles.toastWrap, { opacity: progress, transform: [{ translateY }] }]}
    >
      <Text style={[styles.toast, fill]}>{message}</Text>
    </Animated.View>
  );
}
